import { getErrorMessage } from "../utils/errorMessage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { supportTicketsAPI } from "../api/client";
import { normalizeRole } from "../utils/accessControl";
import ConfirmModal from "../components/ConfirmModal";
import "./SupportTickets.css";

const CATEGORIES = [
  ["access", "Access Request"],
  ["role_permission", "Role / Permission"],
  ["technical_issue", "Technical Issue"],
  ["data_issue", "Data Issue"],
  ["feature_request", "Feature Request"],
  ["other", "Other"],
];

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In progress",
  waiting_for_user: "Waiting for user",
  resolved: "Resolved",
  closed: "Closed",
};

const EMPTY_FORM = {
  category: "access",
  priority: "medium",
  subject: "",
  description: "",
};

const categoryLabel = (value) => CATEGORIES.find(([key]) => key === value)?.[1] || value;

// The assign dropdown now lists both Developers and the Developer Head, so
// each option is labelled with its role to make the two distinguishable.
const ASSIGNEE_ROLE_LABELS = {
  developer: "Developer",
  developer_head: "Developer Head",
};

const assigneeRoleLabel = (role) => ASSIGNEE_ROLE_LABELS[normalizeRole(role)] || "Developer";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SupportTickets({ user }) {
  const [tickets, setTickets] = useState([]);
  const [canAssign, setCanAssign] = useState(false);
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [assignPick, setAssignPick] = useState({});
  const [closeComment, setCloseComment] = useState({});
  const [working, setWorking] = useState(null);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [historyTicket, setHistoryTicket] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Anyone who can hold an assigned ticket needs the "Assigned to me" view to
  // resolve and close it. That is both Developer and Developer Head - a head
  // can be assigned their own ticket, and the backend close gate is
  // record-level (assigned_to_user_id === req.user.id), not role-based.
  const canWorkAssignedTickets =
    normalizeRole(user?.role) === "developer" ||
    normalizeRole(user?.role) === "developer_head";
  // Mirror of the backend rule: only the Developer Head can assign or delete
  // tickets. Enforced here in the UI so no other role (Super Admin / Developer
  // Head / any full-access tier) even sees the controls, and the controller
  // re-checks on the server so nothing can bypass it.
  const isDeveloperHead = (candidate) => normalizeRole(candidate?.role) === "developer_head";
  const ticketStatusLabel = (status) => STATUS_LABELS[status] || status;

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await supportTicketsAPI.getAll({
        status: statusFilter,
        category: categoryFilter,
        search: search.trim() || undefined,
        assigned_to_me: activeTab === "assigned" ? "true" : undefined,
      });
      setTickets(response.data?.tickets || []);
      setCanAssign(Boolean(response.data?.canAssign));
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to load support tickets"));
    } finally {
      setLoading(false);
    }
  }, [activeTab, categoryFilter, search, statusFilter]);

  useEffect(() => {
    const timer = window.setTimeout(loadTickets, search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [loadTickets, search]);

  useEffect(() => {
    if (!canAssign) return;
    supportTicketsAPI
      .getDevelopers()
      .then((response) => setDevelopers(response.data?.assignees || []))
      .catch(() => setDevelopers([]));
  }, [canAssign]);

  const stats = useMemo(() => ({
    total: tickets.length,
    open: tickets.filter((ticket) => ticket.status === "open").length,
    active: tickets.filter((ticket) => ["in_progress", "waiting_for_user"].includes(ticket.status)).length,
    resolved: tickets.filter((ticket) => ["resolved", "closed"].includes(ticket.status)).length,
  }), [tickets]);

  const submitTicket = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await supportTicketsAPI.create(form);
      toast.success(response.data?.message || "Support ticket created");
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadTickets();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to create support ticket"));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTicket = (ticket) => {
    setExpandedId((current) => (current === ticket.id ? null : ticket.id));
  };

  const assignUser = async (ticketId) => {
    const userId = assignPick[ticketId];
    if (!userId) {
      toast.error("Select a developer to assign");
      return;
    }
    setWorking(ticketId);
    try {
      const response = await supportTicketsAPI.assign(ticketId, userId);
      toast.success(response.data?.message || "Ticket assigned");
      await loadTickets();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to assign support ticket"));
    } finally {
      setWorking(null);
    }
  };

  // View: every role can open the ticket in a read-only viewer. The expanded
  // shortcut is compact; the dedicated "View timeline" modal holds the full
  // detail + the timeline so Developer Head and Developers get the whole
  // picture of what happened, when, and by whom before they act.
  const handleView = async (ticket) => {
    setWorking(ticket.id);
    setHistoryLoading(true);
    setHistoryTicket(ticket);
    try {
      const [detailResponse, historyResponse] = await Promise.all([
        supportTicketsAPI.getById(ticket.id),
        supportTicketsAPI.getHistory(ticket.id),
      ]);
      setHistoryTicket(detailResponse.data?.ticket || ticket);
      setHistoryEntries(historyResponse.data?.history || []);
      setHistoryOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to load ticket history"));
    } finally {
      setWorking(null);
      setHistoryLoading(false);
    }
  };

  const removeTicket = (ticket) => {
    if (!isDeveloperHead(user)) {
      toast.error("Only the Developer Head can delete tickets");
      return;
    }
    setTicketToDelete(ticket);
  };

  const confirmRemoveTicket = async () => {
    if (!ticketToDelete || working) return;
    const ticket = ticketToDelete;
    setWorking(ticket.id);
    try {
      const response = await supportTicketsAPI.remove(ticket.id);
      toast.success(response.data?.message || "Ticket deleted");
      setTicketToDelete(null);
      await loadTickets();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to delete support ticket"));
    } finally {
      setWorking(null);
    }
  };

  const markResolved = async (ticket) => {
    setWorking(ticket.id);
    try {
      const response = await supportTicketsAPI.markStatus(ticket.id, {
        status: "resolved",
        developer_response: closeComment[ticket.id] || "",
      });
      toast.success(response.data?.message || "Ticket marked as resolved");
      await loadTickets();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to update ticket"));
    } finally {
      setWorking(null);
    }
  };

  const closeTicket = async (ticket) => {
    const responseText = (closeComment[ticket.id] || "").trim();
    if (responseText.length < 10) {
      toast.error("Describe the resolution before closing the ticket");
      return;
    }
    setWorking(ticket.id);
    try {
      const response = await supportTicketsAPI.markStatus(ticket.id, {
        status: "closed",
        developer_response: responseText,
      });
      toast.success(response.data?.message || "Ticket closed");
      await loadTickets();
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to close ticket"));
    } finally {
      setWorking(null);
    }
  };

  const renderReadOnly = (ticket) => {
    if (ticket.developer_response) {
      return (
        <div className="support-response"><span>Response from {ticket.responded_by_name || "Support team"}</span><p>{ticket.developer_response}</p><small>{formatDate(ticket.responded_at)}</small></div>
      );
    }
    return (
      <div className="support-awaiting"><span aria-hidden="true">◷</span><div><strong>Awaiting developer response</strong><p>The support team will update this ticket after reviewing it.</p></div></div>
    );
  };

  // All tickets tab: Development Head assigns/reassigns from here; everyone
  // else gets a read-only view so the whole ticket board stays visible.
  const renderAssignPanel = (ticket) => {
    return (
      <div className="support-resolution-panel">
        <h4>Assign to a developer</h4>
        <div className="support-admin-grid">
          <label className="support-full-field">
            Developer
            <select
              value={assignPick[ticket.id] || ""}
              onChange={(event) => setAssignPick({ ...assignPick, [ticket.id]: Number(event.target.value) })}
              disabled={working === ticket.id}
            >
              <option value="">{ticket.assigned_to_user_id ? "Reassign to…" : "Select a developer…"}</option>
              {developers.map((developer) => (
                <option key={developer.id} value={developer.id}>
                  {developer.name} — {assigneeRoleLabel(developer.role)} ({developer.email})
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="support-form-actions">
          {ticket.assigned_to_name && <span>Currently assigned to <strong>{ticket.assigned_to_name}</strong></span>}
          <button className="support-primary-btn" type="button" disabled={working === ticket.id} onClick={() => assignUser(ticket.id)}>
            {working === ticket.id ? "Assigning…" : ticket.assigned_to_user_id ? "Reassign ticket" : "Assign ticket"}
          </button>
        </div>
      </div>
    );
  };

  // Assigned to me tab: the assignee marks tickets resolved (with a note) and
  // closes them with a resolution response. Reachable for any role that can
  // hold an assignment — Developer or Developer Head.
  const renderDeveloperPanel = (ticket) => {
    if (ticket.status === "closed") return renderReadOnly(ticket);

    if (ticket.status === "resolved") {
      return (
        <div className="support-resolution-panel">
          <h4>Close this ticket</h4>
          <div className="support-admin-grid">
            <label className="support-full-field">
              Resolution note
              <textarea
                rows={4}
                maxLength={5000}
                value={closeComment[ticket.id] || ""}
                placeholder="Describe how this was resolved so the requester knows the outcome."
                onChange={(event) => setCloseComment({ ...closeComment, [ticket.id]: event.target.value })}
              />
            </label>
          </div>
          <div className="support-form-actions">
            <span>Closing as <strong>{user?.name || "you"}</strong></span>
            <button className="support-primary-btn" type="button" disabled={working === ticket.id} onClick={() => closeTicket(ticket)}>
              {working === ticket.id ? "Closing…" : "Close ticket"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="support-resolution-panel">
        <h4>Working on this ticket</h4>
        <div className="support-admin-grid">
          <label className="support-full-field">
            Progress note <small>(optional)</small>
            <textarea
              rows={4}
              maxLength={5000}
              value={closeComment[ticket.id] || ""}
              placeholder="Add a note about progress, next steps, or the fix you applied."
              onChange={(event) => setCloseComment({ ...closeComment, [ticket.id]: event.target.value })}
            />
          </label>
        </div>
        <div className="support-form-actions">
          <span>Responding as <strong>{user?.name || "you"}</strong></span>
          <button className="support-primary-btn" type="button" disabled={working === ticket.id} onClick={() => markResolved(ticket)}>
            {working === ticket.id ? "Saving…" : "Mark as resolved"}
          </button>
        </div>
      </div>
    );
  };

  const renderWorkPanel = (ticket) => {
    if (activeTab === "assigned") return renderDeveloperPanel(ticket);
    if (canAssign && ticket.status !== "closed") return renderAssignPanel(ticket);
    return renderReadOnly(ticket);
  };

  const heroText = () => {
    if (activeTab === "assigned") return "Tickets assigned to you. Mark them resolved and close them with a resolution.";
    if (canAssign) return "Review the ticket board, assign queries to developers, and track them to closure.";
    return "Every ticket raised appears here so you can see what the team is already working on before raising a new one.";
  };

  // Timeline modal footer uses strong approval language so that closing it is
  // the only way to leave the read-only view without accidentally raising a
  // duplicate ticket for the same query.
  const viewPanel = historyOpen ? (
    <div
      className="support-modal-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setHistoryOpen(false);
      }}
    >
      <div className="support-modal" role="dialog" aria-modal="true" aria-labelledby="support-history-title">
        <div className="support-modal-header">
          <div>
            <span className="support-modal-eyebrow">Ticket {historyTicket?.ticket_number || ""}</span>
            <h3 id="support-history-title">{historyTicket?.subject || "Support ticket"}</h3>
          </div>
          <button className="support-modal-close" type="button" aria-label="Close timeline" onClick={() => setHistoryOpen(false)}>×</button>
        </div>
        <div className="support-modal-body">
          <div className="support-history-meta">
            <span>Raised by <strong>{historyTicket?.requester_name}</strong> on {formatDate(historyTicket?.created_at)}</span>
            <span>Current status: <strong>{ticketStatusLabel(historyTicket?.status)}</strong></span>
            <span>{historyTicket?.assigned_to_name ? <>Assigned to <strong>{historyTicket.assigned_to_name}</strong></> : "Not assigned yet"}</span>
          </div>

          {historyLoading ? (
            <div className="support-history-loading"><div className="support-spinner" /><span>Loading ticket timeline…</span></div>
          ) : historyEntries.length === 0 ? (
            <div className="support-history-empty">
              <p>No timeline entries yet — the first event lands here when this ticket is assigned or responded to.</p>
            </div>
          ) : (
            <div className="support-timeline">
              {historyEntries.map((entry) => (
                <div className={`support-timeline-entry ${entry.entry_type}`} key={entry.id}>
                  <div className="support-timeline-node" aria-hidden="true" />
                  <div className="support-timeline-content">
                    <div className="support-timeline-head">
                      <strong>{entry.title}</strong>
                      <time>{formatDate(entry.created_at)}</time>
                    </div>
                    <span className="support-timeline-byline">by {entry.actor_name || "Support team"}</span>
                    {entry.description && <p>{entry.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="support-modal-footer">
          <span>Read-only — {isDeveloperHead(user) ? "only you can delete a ticket (use the Delete button on the board)." : "raise a new ticket only for something not already on the board."}</span>
          <button className="support-primary-btn" type="button" onClick={() => setHistoryOpen(false)}>Close</button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <section className="support-page">
      <div className="support-hero">
        <div>
          <span className="support-eyebrow">Developer support desk</span>
          <h1>Support Tickets</h1>
          <p>{heroText()}</p>
        </div>
        <button className="support-primary-btn" type="button" onClick={() => setShowForm((value) => !value)}>
          <span aria-hidden="true">{showForm ? "×" : "+"}</span>
          {showForm ? "Close form" : "Raise a ticket"}
        </button>
      </div>

      <div className="support-tabs" role="tablist" aria-label="Ticket views">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "all"}
          className={`support-tab ${activeTab === "all" ? "active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          All tickets
        </button>
        {canWorkAssignedTickets && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "assigned"}
            className={`support-tab ${activeTab === "assigned" ? "active" : ""}`}
            onClick={() => setActiveTab("assigned")}
          >
            Assigned to me
          </button>
        )}
      </div>

      <div className="support-stats" aria-label="Ticket summary">
        <div className="support-stat"><span>{activeTab === "assigned" ? "Assigned to me" : "All tickets"}</span><strong>{stats.total}</strong></div>
        <div className="support-stat open"><span>Open</span><strong>{stats.open}</strong></div>
        <div className="support-stat active"><span>In progress</span><strong>{stats.active}</strong></div>
        <div className="support-stat resolved"><span>Completed</span><strong>{stats.resolved}</strong></div>
      </div>

      {showForm && (
        <form className="support-create-card" onSubmit={submitTicket}>
          <div className="support-card-heading">
            <div className="support-heading-icon" aria-hidden="true">?</div>
            <div><h2>Tell us what you need</h2><p>Check the board first — if it is already raised, track that ticket instead of creating a duplicate.</p></div>
          </div>
          <div className="support-form-grid">
            <label>
              Category
              <select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>
                {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>
              Priority
              <select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical — work blocked</option>
              </select>
            </label>
            <label className="support-full-field">
              Subject
              <input
                value={form.subject}
                maxLength={180}
                minLength={5}
                required
                placeholder="Example: Unable to access the Project MIS tab"
                onChange={(event) => setForm({ ...form, subject: event.target.value })}
              />
            </label>
            <label className="support-full-field">
              Description
              <textarea
                value={form.description}
                maxLength={5000}
                minLength={10}
                required
                rows={5}
                placeholder="What were you trying to do? What happened, and when did it start?"
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
              <small>{form.description.length}/5000 characters</small>
            </label>
          </div>
          <div className="support-form-actions">
            <span>Submitting as <strong>{user?.name || "Current user"}</strong></span>
            <button className="support-primary-btn" type="submit" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit ticket"}
            </button>
          </div>
        </form>
      )}

      <div className="support-toolbar">
        <div className="support-search">
          <span aria-hidden="true">⌕</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search ticket, subject, or requester" aria-label="Search tickets" />
        </div>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} aria-label="Filter by category">
          <option value="all">All categories</option>
          {CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      <div className="support-list">
        {loading ? (
          <div className="support-empty"><div className="support-spinner" /><h3>Loading tickets</h3></div>
        ) : tickets.length === 0 ? (
          <div className="support-empty"><div className="support-empty-icon">✓</div><h3>No tickets found</h3>
            <p>{activeTab === "assigned" ? "You have no tickets assigned to you yet." : "Raise a ticket when you need help from the development team."}</p>
          </div>
        ) : tickets.map((ticket) => {
          const expanded = expandedId === ticket.id;
          return (
            <article className={`support-ticket ${expanded ? "expanded" : ""}`} key={ticket.id}>
              <button className="support-ticket-summary" type="button" onClick={() => toggleTicket(ticket)} aria-expanded={expanded}>
                <div className={`support-category-icon ${ticket.category}`} aria-hidden="true">
                  {ticket.category === "access" ? "↗" : ticket.category === "role_permission" ? "♙" : ticket.category === "technical_issue" ? "⚙" : "!"}
                </div>
                <div className="support-ticket-main">
                  <div className="support-ticket-meta">
                    <strong>{ticket.ticket_number}</strong>
                    <span>{categoryLabel(ticket.category)}</span>
                    <span>{ticket.requester_name}</span>
                  </div>
                  <h3>{ticket.subject}</h3>
                  <p>{ticket.description}</p>
                </div>
                <div className="support-ticket-side">
                  <span className={`support-status ${ticket.status}`}>{STATUS_LABELS[ticket.status]}</span>
                  <span className={`support-priority ${ticket.priority}`}>{ticket.priority}</span>
                  <time>{formatDate(ticket.created_at)}</time>
                  <span className={`support-chevron ${expanded ? "up" : ""}`}>⌄</span>
                </div>
              </button>

              {expanded && (
                <div className="support-ticket-detail">
                  <div className="support-detail-info">
                    <div><span>Requested by</span><strong>{ticket.requester_name}</strong><small>{ticket.requester_email}</small></div>
                    <div><span>Current role</span><strong>{ticket.requester_role || "—"}</strong></div>
                    <div><span>Assigned to</span><strong>{ticket.assigned_to_name || "Not assigned"}</strong></div>
                    <div><span>Last updated</span><strong>{formatDate(ticket.updated_at)}</strong></div>
                  </div>

                  {renderWorkPanel(ticket)}

                  <div className="support-ticket-actions">
                    <button className="support-link-btn" type="button" onClick={() => handleView(ticket)}>
                      View timeline
                    </button>
                    {isDeveloperHead(user) && (
                      <button className="support-link-btn danger" type="button" onClick={() => removeTicket(ticket)}>
                        Delete ticket
                      </button>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {viewPanel}
      <ConfirmModal
        isOpen={!!ticketToDelete}
        variant="danger"
        title="Delete ticket?"
        message={ticketToDelete ? `Delete ${ticketToDelete.ticket_number}? This permanently removes the ticket and its entire history. This cannot be undone.` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={!!working}
        loadingText="Deleting..."
        onConfirm={confirmRemoveTicket}
        onCancel={() => setTicketToDelete(null)}
      />
    </section>
  );
}

export default SupportTickets;